import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstaller } from '../../contexts/InstallerContext';
import SplashScreen from './SplashScreen';

const SPLASH_SHOWN_KEY = 'installer_splash_shown';
const SPLASH_DURATION_MS = 10 * 60 * 1000; // 10 minutes

export default function InstallerLayout({ children, showSplash = false }) {
  const { signOut } = useAuth();
  const installer = useInstaller();
  const [displaySplash, setDisplaySplash] = useState(false);

  useEffect(() => {
    if (showSplash) {
      const lastShown = localStorage.getItem(SPLASH_SHOWN_KEY);
      const now = Date.now();

      if (!lastShown || (now - parseInt(lastShown)) > SPLASH_DURATION_MS) {
        setDisplaySplash(true);
        localStorage.setItem(SPLASH_SHOWN_KEY, now.toString());
      }
    }
  }, [showSplash]);

  const getUserInitials = () => {
    if (!installer.name) return 'I';
    const parts = installer.name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return installer.name.slice(0, 2).toUpperCase();
  };

  if (displaySplash) {
    return <SplashScreen installerName={installer.name} onComplete={() => setDisplaySplash(false)} />;
  }

  return (
    <>
      <style>
        {`
          /* Installer portal only - prevent iOS zoom and horizontal scroll */
          .installer-portal input,
          .installer-portal textarea,
          .installer-portal select {
            font-size: 16px !important;
          }
          
          .installer-portal {
            overflow-x: hidden;
            width: 100%;
            max-width: 100vw;
          }
          
          .installer-portal * {
            max-width: 100%;
          }
        `}
      </style>

      <div className="installer-portal" style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f2ed 0%, #faf8f5 100%)'
      }}>
        <header style={{
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          overflow: 'hidden',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            left: '-10px',
            right: '-10px',
            bottom: '-10px',
            backgroundImage: 'url(/brand/shop-bg.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'blur(4px)',
            transform: 'scale(1.1)',
            zIndex: 1
          }} />

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(139, 69, 19, 0.4) 100%)',
            zIndex: 2
          }} />

          <div style={{
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.8)'
            }}>
              {getUserInitials()}
            </div>

            <div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}>
                {installer.name}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.9)',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                fontWeight: '500'
              }}>
                ⚠️ Internal Use Only
              </div>
            </div>
          </div>

          <button
            onClick={signOut}
            style={{
              position: 'relative',
              zIndex: 3,
              padding: '10px 20px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.2s',
              minWidth: '44px',
              minHeight: '44px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Sign Out
          </button>
        </header>

        <main style={{
          flex: 1,
          padding: '20px',
          overflowY: 'auto'
        }}>
          {children}
        </main>

        <footer style={{
          height: '70px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 20px',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            left: '-10px',
            right: '-10px',
            bottom: '-10px',
            backgroundImage: 'url(/brand/shop-bg.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'blur(4px)',
            transform: 'scale(1.1)',
            zIndex: 1
          }} />

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(139, 69, 19, 0.4) 100%)',
            zIndex: 2
          }} />

          <div style={{
            position: 'relative',
            zIndex: 3,
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.7)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              fontWeight: '500',
              marginBottom: '4px'
            }}>
              © {new Date().getFullYear()} The Window Place LLC
            </div>
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.85)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <span style={{ fontWeight: '500' }}>WEB-APP design & development —</span>
              <span style={{
                fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                fontSize: '15px',
                fontWeight: '400',
                color: 'white',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
                letterSpacing: '0.5px'
              }}>
                Noah Kwizera
              </span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}